import React, { useRef, useState } from 'react';
import '../styles/SourceSetupFlows.css';
import { Messages, Twilio } from '../assets/icons';
import EmailProviderIcons from './EmailProviderIcons';
import { cleanPhoneNumber, formatPhoneNumber } from '../functions';
import Badge from './Badge';
import Button from './Button';
import Input from './Input';
import { HugeiconsIcon } from '@hugeicons/react';
import { CheckmarkBadgeIcon, CancelCircleIcon } from '@hugeicons/core-free-icons';

function UnipileTrustLine() {
    const [open, setOpen] = useState(false);
    const btnRef = useRef(null);
    return (
        <div className="source-setup-trust">
            <HugeiconsIcon icon={CheckmarkBadgeIcon} size={16} color="var(--jade-03)" strokeWidth={1.5} className="source-setup-trust-icon" />
            <span className="xs-label colour-secondary">Secure connection powered by Unipile</span>
            <div className="source-setup-trust-info">
                <button
                    ref={btnRef}
                    type="button"
                    className="source-setup-trust-btn"
                    aria-label="Why Unipile?"
                    onClick={() => setOpen(o => !o)}
                >
                    &#9432;
                </button>
                {open && (
                    <>
                        <div className="source-setup-trust-backdrop" onClick={() => setOpen(false)} />
                        <div className="source-setup-trust-popover" role="tooltip">
                            <p className="xs-label-bold colour-primary">Why Unipile?</p>
                            <p className="xs-label colour-secondary">We never store your email password or read personal emails. We only scan for real estate deal content. Connection secured by Unipile.</p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function PrimaryButton({ text, onClick, disabled = false, spinner = false }) {
    return (
        <Button
            buttonType="primary-button"
            size="large"
            text={text}
            cta={onClick}
            disabled={disabled}
            leftIcon={null}
            leftIconClass=""
            rightIcon={null}
            rightIconClass=""
            alt=""
            standardIcon={false}
            badge={false}
            badgeVal={null}
            badgeBG={null}
            buttonRef={null}
            spinner={spinner}
            goldAccent={false}
        />
    );
}

function PhoneInput({ label, value, onChange, disabled }) {
    return (
        <Input
            label={label}
            labelType="inside"
            secondaryLabelGrey={null}
            info={false}
            value={value}
            onChange={(event) => onChange(formatPhoneNumber(event.target.value))}
            placeholder="(555) 000-0000"
            type="tel"
            leftUnit={null}
            rightUnit={null}
            unitSwitch={false}
            leftUnitSelected={true}
            unitSwitchFunc={null}
            disabled={disabled}
            multiline={false}
            error={false}
            onMouseEnter={null}
            onMouseLeave={null}
            reset={false}
            resetFunc={null}
            resetPosition={null}
            focusMultiline={false}
            prompt={false}
        />
    );
}

export function EmailSourceSetupFlow({
    connectedAccount,
    connecting,
    awaitingConfirmation = false,
    loading = false,
    error,
    continueText = 'Continue',
    onBack,
    onConnect,
    onComplete,
    onSkip
}) {
    if (connectedAccount) {
        return (
            <div className="source-setup-flow source-setup-connected">
                <div className="source-setup-connected-icon" aria-hidden="true">
                    <svg viewBox="0 0 48 48" fill="none" className="source-setup-connected-check">
                        <circle cx="24" cy="24" r="24" fill="var(--jade-01)" />
                        <path d="M14 24l7 7 13-13" stroke="var(--jade-03)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
                <div className="source-setup-connected-body">
                    <Badge
                        bgColour="bg-colour-jade-01"
                        text="Connected"
                        textClass="xs-label-bold colour-green"
                        statusDot={false}
                        statusDotColour={null}
                        icon={null}
                        iconLeft={false}
                        tooltip={false}
                        tooltipBody={null}
                        tooltipPosition={null}
                    />
                    <h1 className="heading-large-bold colour-primary">Email connected</h1>
                    <span className="body-regular colour-secondary">{connectedAccount.email || 'Your inbox'}</span>
                </div>
                <div className="source-setup-actions">
                    <PrimaryButton text={continueText} onClick={onComplete} />
                </div>
            </div>
        );
    }

    return (
        <div className="source-setup-flow">
            {onBack ? (
                <button type="button" className="source-setup-back label-regular colour-secondary cursor-pointer" onClick={onBack}>
                    &larr; Back
                </button>
            ) : null}
            <EmailProviderIcons />
            <div className="source-setup-header">
                <h1 className="heading-large-bold colour-primary">Connect your real estate inbox</h1>
                <span className="body-regular colour-secondary">We scan every new email automatically and <em>only</em> save real estate deals</span>
            </div>
            <div className="source-setup-examples">
                <div className="source-setup-example source-setup-example-saved">
                    <span className="xs-label-bold source-setup-example-tag-saved">
                        <HugeiconsIcon icon={CheckmarkBadgeIcon} size={12} color="currentColor" strokeWidth={2} />
                        SAVED
                    </span>
                    <span className="body-regular colour-primary">"234 Main St, Phoenix - 3/2 - $185k cash, needs new roof"</span>
                </div>
                <div className="source-setup-example source-setup-example-ignored">
                    <span className="xs-label-bold source-setup-example-tag-ignored">
                        <HugeiconsIcon icon={CancelCircleIcon} size={12} color="currentColor" strokeWidth={2} />
                        IGNORED
                    </span>
                    <span className="body-regular colour-secondary">"Lunch tomorrow at noon?"</span>
                </div>
                <div className="source-setup-example source-setup-example-ignored">
                    <span className="xs-label-bold source-setup-example-tag-ignored">
                        <HugeiconsIcon icon={CancelCircleIcon} size={12} color="currentColor" strokeWidth={2} />
                        IGNORED
                    </span>
                    <span className="body-regular colour-secondary">"Your Amazon order has shipped"</span>
                </div>
            </div>
            <div className="source-setup-actions">
                <PrimaryButton
                    text={awaitingConfirmation ? 'Finishing connection...' : connecting ? 'Redirecting...' : loading ? 'Setting up...' : 'Connect email'}
                    onClick={onConnect}
                    disabled={connecting || loading || awaitingConfirmation}
                    spinner={connecting || loading || awaitingConfirmation}
                />
                {error ? <span className="label-regular colour-error text-align-center">{error}</span> : null}
                {onSkip ? (
                    <button type="button" className="source-setup-link label-regular colour-secondary cursor-pointer" onClick={onSkip}>
                        Skip, I'll set this up later
                    </button>
                ) : null}
                <UnipileTrustLine />
            </div>
        </div>
    );
}

export function SmsSourceSetupFlow({
    subStep,
    path,
    authorizedSenderNumber,
    setAuthorizedSenderNumber,
    personalNumber,
    setPersonalNumber,
    saving,
    error,
    webhookUrl,
    clozersNumber,
    webhookCopied,
    numberCopied,
    contactSaved,
    onBack,
    onSelectPath,
    onSaveAuthorizedSender,
    onSavePersonal,
    onCopyWebhook,
    onCopyNumber,
    onSaveContact,
    onOpenScreenshot,
    onComplete,
    onSkip
}) {
    return (
        <div className="source-setup-flow">
            <button type="button" className="source-setup-back label-regular colour-secondary cursor-pointer" onClick={onBack}>
                &larr; Back
            </button>

            {subStep === 0 ? (
                <>
                    <div className="source-setup-header">
                        <h1 className="heading-large-bold colour-primary">Where are your deals texted?</h1>
                    </div>
                    <div className="source-setup-options">
                        <button type="button" className="source-setup-option cursor-pointer" onClick={() => onSelectPath('twilio')}>
                            <div className="source-setup-option-icon"><Twilio /></div>
                            <div className="source-setup-option-text">
                                <span className="body-semibold colour-primary">A Twilio inbox</span>
                                <span className="label-regular colour-secondary">Automatic - wholesalers text deals directly in</span>
                            </div>
                            <Badge
                                bgColour="bg-colour-jade-01"
                                text="Recommended"
                                textClass="xs-label-bold colour-jade-03"
                                statusDot={false}
                                statusDotColour={null}
                                icon={null}
                                iconLeft={false}
                                tooltip={false}
                                tooltipBody={null}
                                tooltipPosition={null}
                            />
                        </button>
                        <button type="button" className="source-setup-option cursor-pointer" onClick={() => onSelectPath('forward')}>
                            <div className="source-setup-option-icon"><Messages /></div>
                            <div className="source-setup-option-text">
                                <span className="body-semibold colour-primary">My personal phone</span>
                                <span className="label-regular colour-secondary">Forward deal texts to Clozers manually</span>
                            </div>
                        </button>
                    </div>
                    {onSkip ? (
                        <div className="source-setup-actions">
                            <button type="button" className="source-setup-link label-regular colour-secondary cursor-pointer" onClick={onSkip}>
                                Skip, I'll set this up later
                            </button>
                        </div>
                    ) : null}
                </>
            ) : null}

            {subStep === 1 && path === 'twilio' ? (
                <>
                    <div className="source-setup-header">
                        <h1 className="heading-large-bold colour-primary">Set up your Twilio inbox</h1>
                        <span className="body-regular colour-secondary">Point your inbox at Clozers, then authorize a number that sends you deals.</span>
                    </div>
                    <div className="source-setup-steps">
                        <div className="source-setup-step">
                            <span className="source-setup-step-num xs-label-bold">1</span>
                            <div className="source-setup-step-body">
                                <a href="https://console.twilio.com/us1/develop/phone-numbers/manage/active" target="_blank" rel="noopener noreferrer" className="body-regular colour-primary underline">
                                    Open Twilio console &rarr;
                                </a>
                                <span className="body-regular colour-primary">Select the Twilio inbox where your deals arrive.</span>
                            </div>
                        </div>
                        <div className="source-setup-step">
                            <span className="source-setup-step-num xs-label-bold">2</span>
                            <div className="source-setup-step-body">
                                <span className="body-regular colour-primary">Under <strong>A message comes in</strong>, select <strong>Webhook</strong> and paste this URL:</span>
                                {onOpenScreenshot ? (
                                    <img
                                        src={`${process.env.PUBLIC_URL}/twilio-webhook.png`}
                                        alt="Twilio console showing the A message comes in webhook field"
                                        className="source-setup-twilio-screenshot cursor-pointer"
                                        onClick={onOpenScreenshot}
                                    />
                                ) : null}
                                <div className="source-setup-value-box">
                                    <code className="source-setup-webhook body-regular colour-primary">{webhookUrl}</code>
                                    <button type="button" className="source-setup-copy label-bold colour-primary cursor-pointer" onClick={onCopyWebhook}>
                                        {webhookCopied ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="source-setup-step">
                            <span className="source-setup-step-num xs-label-bold">3</span>
                            <div className="source-setup-step-body">
                                <span className="body-regular colour-primary">Authorize one deal-sender phone number. This is the wholesaler or source texting your Twilio inbox, not your Twilio number.</span>
                                <PhoneInput
                                    label="Authorized sender number"
                                    value={authorizedSenderNumber}
                                    onChange={setAuthorizedSenderNumber}
                                    disabled={saving}
                                />
                            </div>
                        </div>
                    </div>
                    {error ? <span className="label-regular colour-error">{error}</span> : null}
                    <div className="source-setup-actions">
                        <PrimaryButton
                            text={saving ? 'Saving...' : 'Save & finish setup'}
                            onClick={onSaveAuthorizedSender}
                            disabled={cleanPhoneNumber(authorizedSenderNumber).length < 10 || saving}
                            spinner={saving}
                        />
                        {onSkip ? (
                            <button type="button" className="source-setup-link label-regular colour-secondary cursor-pointer" onClick={onSkip}>
                                Skip, I'll set this up later
                            </button>
                        ) : null}
                    </div>
                </>
            ) : null}

            {subStep === 1 && path === 'forward' ? (
                <>
                    <div className="source-setup-header source-setup-icon-header">
                        <Messages />
                        <h1 className="heading-large-bold colour-primary">What's your personal number?</h1>
                    </div>
                    <div className="source-setup-form">
                        <PhoneInput label="Your phone number" value={personalNumber} onChange={setPersonalNumber} disabled={saving} />
                        <span className="label-regular colour-secondary">So we know which account to route your deals to.</span>
                    </div>
                    {error ? <span className="label-regular colour-error">{error}</span> : null}
                    <div className="source-setup-actions">
                        <PrimaryButton
                            text={saving ? 'Saving...' : 'Continue'}
                            onClick={onSavePersonal}
                            disabled={cleanPhoneNumber(personalNumber).length < 10 || saving}
                            spinner={saving}
                        />
                    </div>
                </>
            ) : null}

            {subStep === 2 && path === 'forward' ? (
                <>
                    <div className="source-setup-header">
                        <h1 className="heading-large-bold colour-primary">Forward deals to Clozers</h1>
                    </div>
                    <div className="source-setup-forward">
                        <div className="source-setup-value-box">
                            <span className="heading-small-bold colour-primary">{clozersNumber}</span>
                            <button type="button" className="source-setup-copy label-bold colour-primary cursor-pointer" onClick={onCopyNumber}>
                                {numberCopied ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                        <span className="label-regular colour-secondary text-align-center">Save this number, then forward deal texts to it from your phone.</span>
                    </div>
                    <div className="source-setup-actions">
                        <PrimaryButton
                            text={contactSaved ? 'Saved! Continuing...' : 'Save contact'}
                            onClick={onSaveContact}
                            disabled={contactSaved}
                            spinner={contactSaved}
                        />
                        <button type="button" className="source-setup-link label-regular colour-secondary cursor-pointer" onClick={onComplete}>
                            Continue without saving contact
                        </button>
                    </div>
                </>
            ) : null}
        </div>
    );
}
